import { ModelName } from "./ModelName";
import type { McpConfig, McpPreloadResources } from "./Mcp";
import { SEEDS_DATA_AGENT } from "./personas/seedsDataAgent";
import { BTSAT_AGENT } from "./personas/btsatAgent";
import { BIOTECH_DATA_AGENT } from "./personas/biotechDataAgent";
import { GALILEO_DATA_AGENT } from "./personas/galileoDataAgent";
import { MEDIA_API_AGENT } from "./personas/mediaApiAgent";
import { INACTIVE_PERSONAS } from "./personas/inactive";
import { isLocalOrSandbox } from "../utilities/environment";
import { PRODUCT_NAME } from "../utilities/productName";

export type PersonaMaturity = "stable" | "preview";

/**
 * Reasoning effort levels for adaptive extended thinking. Matches the API's
 * `ThinkingEffort` literal (corteva-chat-api ADR 061).
 */
export type ReasoningEffort = "low" | "medium" | "high" | "xhigh" | "max";

export interface Persona {
  /**
   * Unique identifier for the persona.
   * Originally, the ID was a manually incremented integer but had to change
   * to a UUID to support custom personas.
   */
  id: number | string;
  name: string;
  modelName: ModelName;
  imageCapable: boolean;
  active: boolean;
  systemMessage?: string;
  description?: string;
  welcomeMessage?: string;
  warningMessage?: string;
  examplePrompts?: string[];
  author?: string;
  lastUpdate?: string;
  mcpConfig?: McpConfig;
  mcpPreloadResources?: McpPreloadResources;
  maxRecursionDepth?: number;
  /**
   * Indicates the maturity level of the persona.
   * - "stable": Production-ready personas (e.g., GPT 5, Claude Sonnet 4.5)
   * - "preview": Experimental or beta personas (e.g., Weather MCP Testbot)
   * Defaults to "stable" if not specified.
   */
  maturity?: PersonaMaturity;
  /**
   * Indicates whether the persona has access to Corteva internal data.
   * This can be through MCP tools, or other internal sources.
   * - true: Internal personas (e.g., Seeds Data Agent)
   * - false: Public personas (e.g., GPT, Claude, Gemini)
   * Defaults to false if not specified.
   */
  isInternal?: boolean;
  /**
   * Indicates whether the persona is an image generation model.
   * - true: Image generation personas (e.g., Nano Banana Image Generation)
   * - false: Standard text/multimodal personas
   * Defaults to false if not specified.
   * Displays as "Image Generation" in the UI.
   */
  isImageGenerator?: boolean;
  /**
   * Indicates whether the persona gets code execution via the executeCode MCP server.
   * - true/undefined: Every request (outside incognito) includes executeCode and
   *   the code execution system prompt section (ADR 074)
   * - false: The persona never gets code execution
   */
  codeExecutionCapable?: boolean;
  /**
   * Indicates whether the persona supports JupyterHub tools via the jupyterhub_mcp_shim MCP server.
   * When true, a toggle button will be shown to enable/disable JupyterHub access.
   * - true/undefined: JupyterHub can be enabled for this persona
   * - false: JupyterHub toggle is not available
   */
  jupyterHubCapable?: boolean;
  /**
   * Indicates whether the persona supports task delegation via the subagent MCP server.
   * Opt-in: only personas that explicitly set this to true get the toggle and MCP access.
   * - true: Delegate Task toggle is available for this persona
   * - false/undefined: Delegate Task toggle is not available
   */
  subagentCapable?: boolean;
  /**
   * Controls whether this persona appears in the incognito mode persona selector.
   * Defaults to true if not specified.
   */
  allowIncognito?: boolean;
  /**
   * Reasoning effort for adaptive-capable Claude models (Sonnet 4.6+, Sonnet 5,
   * Opus 4.6+). When set, the request opts into adaptive extended thinking at this
   * effort. When omitted, thinking stays disabled. The API ignores this field for
   * non-Claude models and Claude models <= 4.5. See corteva-chat-api ADR 061.
   */
  reasoningEffort?: ReasoningEffort;
}

export const AVAILABLE_PERSONAS: Persona[] = [
  ...INACTIVE_PERSONAS,
  {
    id: 20,
    name: "Claude Sonnet 5",
    modelName: ModelName.CLAUDE_5_SONNET,
    imageCapable: true,
    active: true,
    subagentCapable: true,
    description:
      "Anthropic's mid-size model with superior intelligence for high-volume uses in coding, in-depth research, agents, & more.",
  },
  {
    id: 21,
    name: "Claude Opus 5",
    modelName: ModelName.CLAUDE_5_OPUS,
    imageCapable: true,
    active: true,
    description:
      "Anthropic’s most intelligent model, useful for coding, enterprise agents, and professional work.",
  },
  {
    id: 23,
    name: "GPT 5.6 Sol",
    modelName: ModelName.GPT_56_SOL,
    imageCapable: true,
    active: true,
    description:
      "OpenAI's flagship model for coding, reasoning, and agentic tasks across domains.",
  },
  {
    id: 24,
    name: "GPT 5.6 Terra",
    modelName: ModelName.GPT_56_TERRA,
    imageCapable: true,
    active: true,
    description: "OpenAI's mid-tier version of GPT for every-day tasks.",
  },
  {
    id: 25,
    name: "GPT 5.6 Luna",
    modelName: ModelName.GPT_56_LUNA,
    imageCapable: true,
    active: true,
    description: "OpenAI's Fastest, most cost-efficient version of GPT.",
  },
  {
    id: 26,
    name: "Weather MCP Testbot",
    modelName: ModelName.CLAUDE_4_6_SONNET,
    imageCapable: true,
    active: isLocalOrSandbox(),
    codeExecutionCapable: false,
    description: `Used for local testing of MCP. Calls the mcp-weather-server which exposes two National Weather Service powered tools. Only active when running ${PRODUCT_NAME} locally.`,
    welcomeMessage: `The Weather MCP Testbot provides a way for ${PRODUCT_NAME} developers to test out ${PRODUCT_NAME}'s MCP support. Modify the mcpConfig in Persona.ts to try out different MCP servers.`,
    examplePrompts: [
      "What is the weather forecast for Boulder, Colorado?",
      "Are there any active weather alerts for New Mexico?",
    ],
    mcpConfig: {
      weather: {
        url: "https://mcp-weather.kube2.research.vylor.com/mcp",
        transport: "streamable_http",
      },
    },
    isInternal: true,
    allowIncognito: false,
    maturity: "preview",
  },
  {
    id: 27,
    name: "Gemini 4 Argon",
    modelName: ModelName.GEMINI_4_ARGON,
    imageCapable: true,
    active: true,
    description:
      "Gemini Argon delivers frontier performance in complex workflows",
  },
  SEEDS_DATA_AGENT,
  {
    id: 29,
    name: "Gemini Nano Banana 2 🍌",
    modelName: ModelName.GEMINI_3_1_FLASH_IMAGE,
    imageCapable: true,
    active: true,
    description:
      "Create an image with Google's Nano Banana 2 image generation model based on Gemini 3.1 Flash.",
    welcomeMessage: "Generate high-quality images from text prompts.",
    examplePrompts: [
      "A picturesque cornfield at sunset with rolling hills",
      "A diagram depicting the major agricultural regions of the US",
      "A cartoon-style drawing of Captain Maize, a superhero corn character",
    ],
    isImageGenerator: true,
    codeExecutionCapable: false,
    allowIncognito: false,
  },
  {
    id: 30,
    name: "Gemini Nano Banana Pro 🍌",
    modelName: ModelName.GEMINI_3_PRO_IMAGE,
    imageCapable: true,
    active: true,
    description:
      "Create an image with Google's Nano Banana Pro image generation model based on Gemini 3 Pro.",
    maturity: "preview",
    welcomeMessage: "Generate high-quality images from text prompts.",
    examplePrompts: [
      "A picturesque cornfield at sunset with rolling hills",
      "A diagram depicting the major agricultural regions of the US",
      "A cartoon-style drawing of Captain Maize, a superhero corn character",
    ],
    isImageGenerator: true,
    codeExecutionCapable: false,
    allowIncognito: false,
  },
  {
    id: 31,
    name: "Gemini 3.8 Flash",
    modelName: ModelName.GEMINI_3_8_FLASH,
    imageCapable: true,
    active: true,
    description:
      "Designed to deliver strong agentic capabilities at substantial speed and value.",
  },
  BIOTECH_DATA_AGENT,
  BTSAT_AGENT,
  GALILEO_DATA_AGENT,
  MEDIA_API_AGENT,
];

/**
 * Id of the persona used when no persona is selected. Looked up by id rather
 * than by position in `AVAILABLE_PERSONAS` so that reordering the array — or
 * moving personas into `INACTIVE_PERSONAS` — cannot silently change the default.
 */
const DEFAULT_PERSONA_ID = 31;

const defaultPersona = AVAILABLE_PERSONAS.find(
  (persona) => persona.id === DEFAULT_PERSONA_ID
);
if (!defaultPersona) {
  throw new Error(
    `No persona is defined with the default persona id ${DEFAULT_PERSONA_ID}.`
  );
}

/**
 * Default persona to use when no persona is selected.
 * May be used as a fallback.
 */
export const DEFAULT_PERSONA: Persona = defaultPersona;

/**
 * Default for Persona.examplePrompts.
 */
export const DEFAULT_EXAMPLE_PROMPTS = [
  "Help me summarize a research paper",
  "Help me write a python function",
  "Help me identify plants in an image",
];

/**
 * Retrieves a persona by its ID.
 *
 * @param {number} id - The ID of the persona to retrieve.
 * @returns {Persona} - The persona with the specified ID, or the default persona if the ID is out of range.
 */
export function getPersonaById(id: Persona["id"]): Persona {
  // Persona IDs are declared as `number | string`, but the db-api stringifies
  // numeric IDs (e.g. `5` arrives as `"5"`). Compare as strings so a numeric
  // id and its stringified form match.
  return (
    AVAILABLE_PERSONAS.find((p) => String(p.id) === String(id)) ??
    DEFAULT_PERSONA
  );
}

/**
 * Retrieves a persona by its name.
 *
 * @param {string} name - The name of the persona to retrieve.
 * @returns {Persona} - The persona with the specified name, or the default persona if not found.
 */
export function getPersonaByName(name: string): Persona {
  const normalizeString = (str: string) =>
    str.trim().replace(/\s+/g, " ").toLowerCase();
  const normalizedName = normalizeString(name);
  const persona = AVAILABLE_PERSONAS.find(
    (p) => normalizeString(p.name) === normalizedName
  );
  return persona ?? DEFAULT_PERSONA;
}

/**
 * Retrieves a list of active personas
 *
 * @returns {Persona[]} - An array of active personas.
 */
export function getActivePersonas(): Persona[] {
  return AVAILABLE_PERSONAS.filter((persona) => persona.active);
}

export function getIncognitoPersonas(): Persona[] {
  return AVAILABLE_PERSONAS.filter(
    (persona) => persona.active && persona.allowIncognito !== false
  );
}
